import {LocalStorage} from "./LocalStorage";
import * as Sentry from "@sentry/react";
import {API_URL} from "../settings/local_settings";

const DEFAULT_CACHE_TTL = 30000; // 30 segundos
const _cache = new Map();    // url -> { data, timestamp }
const _inflight = new Map(); // url -> Promise

export class APIClient {
    constructor(url) {
        this.url = url;
    }

    static clearCache(urlPattern) {
        if (!urlPattern) {
            _cache.clear();
            return;
        }
        for (const key of _cache.keys()) {
            if (key.includes(urlPattern)) _cache.delete(key);
        }
    }

    call(method = 'GET', data = {}, options = {}) {
        const useCache = method === 'GET' && options.cache === true;
        const cacheTTL = options.cacheTTL || DEFAULT_CACHE_TTL;

        if (useCache) {
            const cached = _cache.get(this.url);
            if (cached && (Date.now() - cached.timestamp) < cacheTTL) {
                return Promise.resolve(structuredClone(cached.data));
            }

            const inflight = _inflight.get(this.url);
            if (inflight) {
                return inflight.then(data => data ? structuredClone(data) : data);
            }
        }

        let headers = new Headers();
        const token = LocalStorage.fetchUserToken();
        if (token) {
            headers.append("Authorization", "Token " + token);
        }

        headers.append("Accept", "application/json");
        headers.append("Content-Type", "application/json");

        let requestConf = {
            method: method,
            headers: headers,
        };

        const dataIsNotEmpty = data !== null && data !== undefined && Object.keys(data).length > 0;
        if (dataIsNotEmpty) {
            requestConf['body'] = JSON.stringify(data)
        }
        const request = new Request(this.url, requestConf);

        const promise = fetch(request)
            .then((response) => this.handleResponse(response))
            .then((responseData) => {
                if (useCache && responseData && responseData.status !== 'ERROR') {
                    _cache.set(this.url, { data: responseData, timestamp: Date.now() });
                }
                return responseData;
            })
            .catch(error => {
                Sentry.captureException(error, {
                    extra: {
                        url: this.url,
                        method: method,
                        requestBody: data,
                    }
                });
                // FEDE: Por ahora no devolvemos nada para que no tire ningun alert raro. Vemos en Sentry si se puede identificar y solucionar.
                //return { "status": "ERROR", "error_message": "Error en la conexión" };
            })
            .finally(() => {
                _inflight.delete(this.url);
            });

        if (useCache) {
            _inflight.set(this.url, promise);
        }

        return promise;
    }

    handleResponse(response) {
        if (!response.ok) {
            return response.json().then(errorBody => {
                // Parse and return API-specific error messages, if any
                return {
                    "status": errorBody.status || "ERROR",
                    // "error_message": errorBody.message || "Unknown error occurred.",
                    "error_message": errorBody.detail || errorBody.message || errorBody.error_message || "Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.",
                    "errors": errorBody.errors || [],
                    "error_code": errorBody.status || "ERROR"
                };
            }).catch(() => {
                // Si el backend devuelve HTML (ej: 500 sin JSON), retornar un objeto de error
                return {
                    "status": "ERROR",
                    "error_message": "Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.",
                    "error_code": response.status
                };
            });
        }
        // Process and return successful response
        return response.json();
    }

    download(method = 'GET', defaultFileName, handlerPromise) {
        let headers = new Headers();
        const token = LocalStorage.fetchUserToken();
        if (token) {
            headers.append("Authorization", "Token " + token);
        }

        headers.append("Content-Type", "txt/csv");

        let requestConf = {
            method: method,
            headers: headers,
        };

        const request = new Request(this.url, requestConf);

        return fetch(request)
            .then((response) => {
                if (!response.ok) {
                    response.json().then(errorBody => {
                        // Parse and return API-specific error messages, if any
                        handlerPromise({
                            "status": errorBody.status || "ERROR",
                            // "error_message": errorBody.message || "Unknown error occurred.",
                            "error_message": errorBody.detail || errorBody.message || errorBody.error_message || "Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.",
                            "error_code": errorBody.status || "ERROR"
                        });
                    });

                } else {
                    const filename = this._getFilename (response, defaultFileName);
                    response.blob().then(blob => {
                        this._getFileFromTemporalLink(blob, filename);
                    });
                    handlerPromise({"status": "OK"});
                }
            })
    }

    _getFileFromTemporalLink(blob, filename) {
        let url = window.URL.createObjectURL(blob);
        let tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.download = filename;
        tempLink.click();
    }

    _getFilename(response, defaultFileName) {
        let contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition !== null){
            return contentDisposition.split('filename=')[1];
        }
        else {
            return defaultFileName
        }
    }

    static getAgentSessionHistory(sessionId) {
        const url = `${API_URL}agent/sessions/${sessionId}/`;
        return new APIClient(url).call('GET');
    }

    static getAgentSessions(params = {}) {
        const query = new URLSearchParams();
        if (params.agency_client_id) query.set('agency_client_id', params.agency_client_id);
        if (params.status) query.set('status', params.status);
        if (params.visibility) query.set('visibility', params.visibility);
        const qs = query.toString();
        const url = `${API_URL}agent/sessions/${qs ? '?' + qs : ''}`;
        return new APIClient(url).call('GET');
    }

    static getAgentSessionsForClient(clientId) {
        const url = `${API_URL}agent/sessions/?agency_client_id=${clientId}`;
        return new APIClient(url).call('GET');
    }

    static renameAgentSession(sessionId, name) {
        const url = `${API_URL}agent/sessions/${sessionId}/rename/`;
        return new APIClient(url).call('PATCH', { name });
    }

    static archiveAgentSession(sessionId, archive = true) {
        return new APIClient(`${API_URL}agent/sessions/${sessionId}/archive/`).call('PATCH', { archive });
    }

    static deleteAgentSession(sessionId) {
        return new APIClient(`${API_URL}agent/sessions/${sessionId}/`).call('DELETE');
    }

    static updateAgentSessionVisibility(sessionId, visibility) {
        return new APIClient(`${API_URL}agent/sessions/${sessionId}/visibility/`).call('PATCH', { visibility });
    }

    login(credentials = {}) {
        let headers = new Headers();
        headers.append("Accept", "application/json");
        headers.append("Content-Type", "application/json");

        let requestConf = {
            method: 'POST',
            headers: headers,
        };

        requestConf['body'] = JSON.stringify(credentials);

        const request = new Request(this.url, requestConf);

        return fetch(request)
            .then((response) => {
                if (response.status === 401) {
                    return Promise.resolve('UNAUTHORIZED');
                }
                if (response.status === 201) {
                    return response.json();
                }
            });
    }

}
